import type {Dispatch} from 'react';
import i18next from 'i18next';
import {useCallback, useMemo} from 'react';

import {head, isNonEmpty} from 'web-app/util/non-empty-array';
import {hasValue} from '@famly/stat_ts-utils_has-value';

import type * as Types from '../types';
import * as Tracking from '../tracking';
import * as DateFormatter from '../helpers/date-formatter';
import * as Helpers from '../helpers/helpers';
import {isEndDateValid} from '../helpers/end-date-validation';
import {AUTOMATIC_PRICING_GROUP_ID, BILLING_FREQUENCY_OPTIONS} from '../constants';
import {biweeklyBillingStartDate, calculateBiweeklyCustomPeriodEndDate} from '../helpers/create-billing-periods';

export type Handlers = {
    handleChildrenChange: Types.HandleChildrenChange;
    handleModalToggle: Types.HandleModalToggle;
    handleModalClose: Types.HandleModalClose;
    handleToggleMoreOptions: Types.HandleToggleMoreOptions;
    handleBillingFrequencyChange: Types.HandleBillingFrequencyChange;
    handlePricingGroupChange: Types.HandlePricingGroupChange;
    handleBillingPeriodChange: Types.HandleBillingPeriodChange;
    handleDiscountChange: Types.HandleDiscountChange;
    handleEndDateChange: Types.HandleEndDateChange;
    handleCustomEndPeriodProratedItemUpdate: Types.HandleCustomEndPeriodProratedItemUpdate;
    handleCustomEndPeriodSave: Types.HandleCustomEndPeriodSave;
    handleCustomEndPeriodCancel: Types.HandleCustomEndPeriodCancel;
    handleAllTuitionsUpdate: Types.HandleAllTuitionsUpdate;
    handleAllFeesUpdate: Types.HandleAllFeesUpdate;
    handleAllDiscountsUpdate: Types.HandleAllDiscountsUpdate;
    toggleTuitionFieldAtIndex: Types.ToggleTuitionField;
    handleTuitionsChange: Types.HandleTuitionsChange;
    appendTuitionField: Types.AppendTuitionField;
    removeTuitionField: Types.RemoveTuitionField;
    toggleFeeFieldAtIndex: Types.ToggleFeeField;
    handleFeesChange: Types.HandleFeesChange;
    appendFeeField: Types.AppendFeeField;
    removeFeeField: Types.RemoveFeeField;
    handleCustomStartPeriodChange: Types.HandleCustomStartPeriodChange;
    handleCustomStartPeriodProratedItemUpdate: Types.HandleCustomStartPeriodProratedItemUpdate;
    handleCustomStartPeriodSave: Types.HandleCustomStartPeriodSave;
    handleSaveTuitionPriceOverride: Types.HandleSaveTuitionPriceOverride;
    setUpEmptyPlan: VoidFunction;
    openAddNewTemplateModal: VoidFunction;
    handleAddNewTemplateChange: Types.HandleAddNewTemplateChange;
    handleEmptyPlanSetRange: Types.HandleEmptyPlanSetRange;
    handleCancelEmptyPlanModal: VoidFunction;
    handleNewTemplateNameChange: Types.HandleNewTemplateNameChange;
    handleUpdatePlanValidationErrors: Types.HandleUpdatePlanValidationErrors;
    handleRemoveChildrenWithError: Types.HandleRemoveChildrenWithError;
    handlePlanStartDateChange: Types.HandlePlanStartDateChange;
    handlePlanEndDateChange: Types.HandlePlanEndDateChange;
    setUpBillingFrequency: Types.SetUpBillingFrequency;
};

type Args = {
    dispatch: Dispatch<Types.Action>;
    state: Types.State;
    billingFrequencies: Array<Types.BillingFrequency>;
    pricingGroups: Array<Types.InitialPricingGroup>;
};

export const useHandlers = ({
    dispatch,
    state: {
        tuitionsState: {selectedTuitions},
        selectedBillingType,
        billingFrequencyState: {selectedBillingFrequency},
        billingPeriodState: {selectedBillingPeriod},
        customPeriodState: {customPeriodDates},
        pricingGroupState: {selectedPricingGroup},
        emptyPlan,
    },
    billingFrequencies,
    pricingGroups,
}: Args): Handlers => {
    const handleChildrenChange: Types.HandleChildrenChange = useCallback(
        selectedChildren => {
            if (!selectedChildren) {
                return;
            }
            const childIds = selectedChildren.map(selectedChild => selectedChild.value);
            dispatch({type: 'SELECT_CHILDREN', payload: {selectedChildren: childIds}});
        },
        [dispatch],
    );

    const handleModalToggle: Types.HandleModalToggle = useCallback(
        modal => {
            dispatch({
                type: 'TOGGLE_MODAL',
                payload: modal,
            });
        },
        [dispatch],
    );

    const handleModalClose: Types.HandleModalClose = useCallback(() => {
        handleModalToggle({type: 'none'});
    }, [handleModalToggle]);

    const handleToggleMoreOptions: Types.HandleToggleMoreOptions = useCallback(
        isMoreOptionsOpen => {
            if (!isMoreOptionsOpen) {
                // track only when the more options is opened
                Tracking.newPlanModalMoreOptions();
            }

            dispatch({type: 'TOGGLE_MORE_OPTIONS', payload: !isMoreOptionsOpen});
        },
        [dispatch],
    );

    const handleBillingFrequencyChange: Types.HandleBillingFrequencyChange = useCallback(
        option => {
            if (!hasValue(option)) {
                return;
            }

            dispatch({
                type: 'SELECT_BILLING_FREQUENCY',
                payload: {selectedOption: option, availableBillingFrequencies: billingFrequencies},
            });
        },
        [billingFrequencies, dispatch],
    );

    const handlePricingGroupChange: Types.HandlePricingGroupChange = useCallback(
        (option, allPricingGroups) => {
            if (!hasValue(option)) {
                return;
            }

            dispatch({
                type: 'SELECT_PRICING_GROUP',
                payload: {selectedOption: option.value, allPricingGroups},
            });
        },
        [dispatch],
    );

    const handleBillingPeriodChange: Types.HandleBillingPeriodChange = useCallback(
        selectedBillingPeriodOption => {
            if (selectedBillingPeriodOption?.type === 'custom') {
                handleModalToggle({type: 'startPeriod'});

                Tracking.newPlanModalCustomPeriodSelected();
                return;
            }

            dispatch({
                type: 'SELECT_BILLING_PERIOD',
                payload: {selectedOption: selectedBillingPeriodOption},
            });
        },
        [dispatch, handleModalToggle],
    );

    const handleDiscountChange: Types.HandleDiscountChange = useCallback(
        selectedOptions => {
            if (!selectedOptions) {
                return;
            }

            if (selectedOptions.some(option => option.value === 'newItem')) {
                handleModalToggle({
                    type: 'addDiscount',
                });
            }

            const discountIds = selectedOptions.map(option => option.value);

            dispatch({
                type: 'SET_DISCOUNTS',
                payload: {selectedOptions: discountIds},
            });
        },
        [dispatch, handleModalToggle],
    );

    const handleEndDateChange: Types.HandleEndDateChange = useCallback(
        (endDate, billingFrequencyType, biweeklyBillingStartDate) => {
            if (!hasValue(endDate) || !hasValue(billingFrequencyType)) {
                return;
            }

            if (
                !selectedBillingPeriod?.unformattedStartDate ||
                !isEndDateValid(endDate, selectedBillingPeriod.unformattedStartDate)
            ) {
                dispatch({
                    type: 'SELECT_END_DATE',
                    payload: {type: 'error', message: 'Invalid end date'},
                });
                return;
            }

            dispatch({
                type: 'SELECT_END_DATE',
                payload: {type: 'date', endDate},
            });

            if (!Helpers.isEndOfPeriodDate(endDate, billingFrequencyType, biweeklyBillingStartDate)) {
                dispatch({
                    type: 'TOGGLE_MODAL',
                    payload: {type: 'endPeriod'},
                });
            }
        },
        [dispatch, selectedBillingPeriod?.unformattedStartDate],
    );

    const handleCustomEndPeriodProratedItemUpdate: Types.HandleCustomEndPeriodProratedItemUpdate = useCallback(
        ({id, title, price}) => {
            dispatch({
                type: 'UPDATE_END_PERIOD_PRORATED_ITEM',
                payload: {
                    id,
                    title,
                    price,
                },
            });
        },
        [dispatch],
    );

    const handleCustomEndPeriodSave: Types.HandleCustomEndPeriodSave = useCallback(
        proratedItems => {
            proratedItems.map(handleCustomEndPeriodProratedItemUpdate);
            dispatch({
                type: 'TOGGLE_MODAL',
                payload: {type: 'none'},
            });
        },
        [dispatch, handleCustomEndPeriodProratedItemUpdate],
    );

    const handleCustomEndPeriodCancel: Types.HandleCustomEndPeriodCancel = useCallback(
        () =>
            dispatch({
                type: 'CANCEL_CUSTOM_END_PERIOD',
            }),
        [dispatch],
    );

    const handleAllTuitionsUpdate: Types.HandleAllTuitionsUpdate = useCallback(
        tuition => {
            dispatch({
                type: 'UPDATE_ALL_TUITIONS',
                payload: {newTuition: tuition},
            });
        },
        [dispatch],
    );

    const handleAllFeesUpdate: Types.HandleAllFeesUpdate = useCallback(
        fee => {
            dispatch({
                type: 'UPDATE_ALL_FEES',
                payload: {newFee: fee},
            });
        },
        [dispatch],
    );

    const handleAllDiscountsUpdate: Types.HandleAllDiscountsUpdate = useCallback(
        discount => {
            dispatch({
                type: 'UPDATE_ALL_DISCOUNTS',
                payload: {newDiscount: discount},
            });
        },
        [dispatch],
    );

    const toggleTuitionFieldAtIndex: Types.ToggleTuitionField = useCallback(
        maybeIndex => {
            dispatch({type: 'TOGGLE_TUITION_FIELD', payload: {maybeIndex}});
        },
        [dispatch],
    );

    const handleTuitionsChange: Types.HandleTuitionsChange = useCallback(
        (index, selectedOption) => {
            if (!selectedOption) {
                return;
            }

            const selectTuition: Types.SelectItem = selectedOption => {
                dispatch({
                    type: 'SELECT_TUITION',
                    payload: {
                        selectedOption,
                        index,
                        billingFrequencyOptions: BILLING_FREQUENCY_OPTIONS,
                        availableBillingFrequencies: billingFrequencies,
                    },
                });
            };

            if (selectedOption.type === 'add-new-item') {
                handleModalToggle({
                    type: 'addTuition',
                    payload: {
                        selectItem: selectTuition,
                    },
                });
                return;
            }

            selectTuition(selectedOption);

            toggleTuitionFieldAtIndex(null);
        },
        [billingFrequencies, dispatch, handleModalToggle, toggleTuitionFieldAtIndex],
    );

    const appendTuitionField: Types.AppendTuitionField = useCallback(
        index => {
            dispatch({type: 'ADD_NEW_TUITION_FIELD', payload: {id: ''}});
            toggleTuitionFieldAtIndex(index + 1);
            Tracking.newPlanModalAddTuition();
        },
        [dispatch, toggleTuitionFieldAtIndex],
    );

    const removeTuitionField: Types.RemoveTuitionField = useCallback(
        index => {
            dispatch({type: 'REMOVE_TUITION_FIELD', payload: {index}});
        },
        [dispatch],
    );

    const toggleFeeFieldAtIndex: Types.ToggleFeeField = useCallback(
        maybeIndex => {
            dispatch({type: 'TOGGLE_FEE_FIELD', payload: {maybeIndex}});
        },
        [dispatch],
    );

    const handleFeesChange: Types.HandleFeesChange = useCallback(
        (index, selectedOption) => {
            if (!selectedOption) {
                return;
            }

            const selectFee: Types.SelectItem = selectedOption => {
                dispatch({type: 'SELECT_FEE', payload: {selectedOption, index}});
            };

            if (selectedOption.type === 'add-new-item') {
                handleModalToggle({
                    type: 'addFee',
                    payload: {
                        selectItem: selectFee,
                    },
                });
                return;
            }

            selectFee(selectedOption);
            toggleFeeFieldAtIndex(null);
        },
        [dispatch, handleModalToggle, toggleFeeFieldAtIndex],
    );

    const appendFeeField: Types.AppendFeeField = useCallback(
        index => {
            dispatch({type: 'ADD_NEW_FEE_FIELD', payload: {id: ''}});
            Tracking.newPlanModalAddFee();
            toggleFeeFieldAtIndex(index);
        },
        [dispatch, toggleFeeFieldAtIndex],
    );

    const removeFeeField: Types.RemoveFeeField = useCallback(
        index => {
            dispatch({type: 'REMOVE_FEE_FIELD', payload: {index}});
        },
        [dispatch],
    );

    const handleCustomStartPeriodChange: Types.HandleCustomStartPeriodChange = useCallback(
        startDate => {
            if (!startDate) {
                return;
            }

            const endDate = (() => {
                if (selectedBillingFrequency?.type === 'biweekly') {
                    return calculateBiweeklyCustomPeriodEndDate(
                        biweeklyBillingStartDate({billingFrequencies}),
                        startDate,
                    );
                }
                return startDate.clone().endOf(selectedBillingType === 'weekly' ? 'isoWeek' : 'month');
            })();

            dispatch({
                type: 'UPDATE_CUSTOM_PERIOD_MODAL_STATE',
                payload: {
                    customPeriodDates: {
                        startDate,
                        endDate,
                    },
                },
            });
        },
        [billingFrequencies, dispatch, selectedBillingFrequency?.type, selectedBillingType],
    );

    const handleCustomStartPeriodProratedItemUpdate: Types.HandleCustomStartPeriodProratedItemUpdate = useCallback(
        ({id, title, price}) => {
            dispatch({
                type: 'UPDATE_PRORATED_ITEM',
                payload: {
                    id,
                    title,
                    price,
                },
            });
        },
        [dispatch],
    );

    const handleCustomStartPeriodSave: Types.HandleCustomStartPeriodSave = useCallback(
        proratedItems => {
            if (!customPeriodDates) {
                return;
            }

            proratedItems.map(handleCustomStartPeriodProratedItemUpdate);

            Tracking.newPlanCustomPeriodSaved();
            dispatch({
                type: 'SELECT_BILLING_PERIOD',
                payload: {
                    selectedOption: {
                        type: 'custom',
                        label: i18next.t(
                            'react.simplifiedUsBillingPlan.translations.json.customPeriod.customPeriodOption.label',
                        ),
                        value: 'customPeriod',
                        from: DateFormatter.numericFormat(customPeriodDates.startDate),
                        to: DateFormatter.numericFormat(customPeriodDates.endDate),
                    },
                },
            });

            handleModalClose();
        },
        [customPeriodDates, dispatch, handleCustomStartPeriodProratedItemUpdate, handleModalClose],
    );

    const handleSaveTuitionPriceOverride = useCallback(
        tuitionPriceOverride => {
            Tracking.newPlanSaveOverridePrice();
            dispatch({
                type: 'SAVE_TUITION_PRICE_OVERRIDE',
                payload: {id: tuitionPriceOverride.id, price: tuitionPriceOverride.price},
            });
            handleModalClose();
        },
        [dispatch, handleModalClose],
    );

    const setUpEmptyPlan = useCallback(() => handleModalToggle({type: 'empty-plan'}), [handleModalToggle]);

    const defaultTemplateName = useMemo(() => {
        if (isNonEmpty(selectedTuitions)) {
            const tuitionTitle = head(selectedTuitions).title;
            const pricingGroupTitle =
                hasValue(selectedPricingGroup) && selectedPricingGroup !== AUTOMATIC_PRICING_GROUP_ID
                    ? ` ${pricingGroups.find(option => option.id === selectedPricingGroup)?.title}`
                    : '';

            return `${tuitionTitle}${pricingGroupTitle}`;
        }

        return '';
    }, [pricingGroups, selectedPricingGroup, selectedTuitions]);
    const openAddNewTemplateModal = () => {
        dispatch({type: 'SET_NEW_TEMPLATE_NAME', payload: {newTemplateName: defaultTemplateName}});
        handleModalToggle({type: 'add-template'});
    };

    const handleAddNewTemplateChange: Types.HandleAddNewTemplateChange = useCallback(
        isChecked => {
            dispatch({type: 'TOGGLE_ADD_NEW_TEMPLATE', payload: {shouldAddNewTemplate: isChecked}});
        },
        [dispatch],
    );

    const handleEmptyPlanSetRange: Types.HandleEmptyPlanSetRange = useCallback(
        (startDate, endDate) => {
            dispatch({type: 'SET_EMPTY_PLAN_DATE_RANGE', payload: {startDate, endDate}});
        },
        [dispatch],
    );

    const handleCancelEmptyPlanModal = useCallback(() => {
        // Reset end date and adjust continue accordingly to have a workaround for undoing setting the end date.
        dispatch({
            type: 'SET_EMPTY_PLAN_DATE_RANGE',
            payload: {startDate: emptyPlan.startDate, endDate: null},
        });
        handleModalClose();
    }, [dispatch, emptyPlan.startDate, handleModalClose]);

    const handleNewTemplateNameChange: Types.HandleNewTemplateNameChange = useCallback(
        newTemplateName => dispatch({type: 'SET_NEW_TEMPLATE_NAME', payload: {newTemplateName}}),
        [dispatch],
    );

    const handlePlanStartDateChange: Types.HandlePlanStartDateChange = useCallback(
        startDateOption => {
            if (!startDateOption) {
                return;
            }

            dispatch({
                type: 'EDIT_VIEW_UPDATE_PLAN_START_DATE',
                payload: {startDate: startDateOption.value},
            });
        },
        [dispatch],
    );

    const handlePlanEndDateChange: Types.HandlePlanEndDateChange = useCallback(
        endDateOption => {
            dispatch({
                type: 'EDIT_VIEW_UPDATE_PLAN_END_DATE',
                payload: {endDate: endDateOption?.value ?? null},
            });
        },
        [dispatch],
    );

    const handleUpdatePlanValidationErrors: Types.HandleUpdatePlanValidationErrors = useCallback(
        results => {
            dispatch({type: 'UPDATE_PLAN_VALIDATION_ERRORS', payload: {results}});
        },
        [dispatch],
    );

    const handleRemoveChildrenWithError: Types.HandleRemoveChildrenWithError = useCallback(
        childError => {
            dispatch({type: 'REMOVE_CHILD_WITH_ERROR', payload: {childError}});
        },
        [dispatch],
    );

    const setUpBillingFrequency: Types.SetUpBillingFrequency = useCallback(
        billingFrequencyTypeToBeSetUp => {
            switch (billingFrequencyTypeToBeSetUp) {
                case 'monthly': {
                    handleModalToggle({type: 'monthly'});
                    break;
                }
                case 'weekly':
                    handleModalToggle({type: 'weekly'});
                    break;
                case 'monthly-partial':
                    handleModalToggle({type: 'monthly-partial'});
                    break;
                case 'weekly-partial':
                    handleModalToggle({type: 'weekly-partial'});
                    break;
                case 'biweekly':
                    handleModalToggle({type: 'biweekly'});
                    break;
                case 'biweekly-partial':
                    handleModalToggle({type: 'biweekly-partial'});
                    break;
                default:
                    handleModalToggle({type: 'none'});
                    break;
            }
        },
        [handleModalToggle],
    );

    return {
        handleChildrenChange,
        handleModalToggle,
        handleModalClose,
        handleToggleMoreOptions,
        handleBillingFrequencyChange,
        handlePricingGroupChange,
        handleBillingPeriodChange,
        handleDiscountChange,
        handleEndDateChange,
        handleCustomEndPeriodProratedItemUpdate,
        handleCustomEndPeriodSave,
        handleCustomEndPeriodCancel,
        handleAllTuitionsUpdate,
        handleAllFeesUpdate,
        handleAllDiscountsUpdate,
        toggleTuitionFieldAtIndex,
        handleTuitionsChange,
        appendTuitionField,
        removeTuitionField,
        toggleFeeFieldAtIndex,
        handleFeesChange,
        appendFeeField,
        removeFeeField,
        handleCustomStartPeriodChange,
        handleCustomStartPeriodProratedItemUpdate,
        handleCustomStartPeriodSave,
        handleSaveTuitionPriceOverride,
        setUpEmptyPlan,
        openAddNewTemplateModal,
        handleAddNewTemplateChange,
        handleEmptyPlanSetRange,
        handleCancelEmptyPlanModal,
        handleNewTemplateNameChange,
        handleUpdatePlanValidationErrors,
        handleRemoveChildrenWithError,
        handlePlanStartDateChange,
        handlePlanEndDateChange,
        setUpBillingFrequency,
    };
};
